/**
 * SSE 스트리밍 관련 공통 유틸리티
 */

/** 토큰 갱신 후 재시도를 위한 커스텀 에러 클래스 */
export class TokenRenewedError extends Error {
  constructor() {
    super('Token renewed');
    this.name = 'TokenRenewedError';
  }
}

/** API 재시도 최대 횟수 */
export const MAX_API_RETRY_COUNT = 3;

/** SSE 재연결 최대 횟수 */
export const MAX_SSE_RECONNECT_COUNT = 5;

/** 재연결 대기 시간 (ms) - 지수 백오프 적용, 최대 30초 */
export const getReconnectDelay = (attempt: number): number =>
  Math.min(1000 * Math.pow(2, attempt), 30000);

/** SSE Idle Timeout (ms) - 마지막 이벤트 후 이 시간 동안 응답 없으면 타임아웃 */
export const SSE_IDLE_TIMEOUT = 30000;

/** SSE 에러 메시지 기본값 */
export const SSE_ERROR_MESSAGES = {
  CONNECTION_LOST: '연결이 끊어졌습니다. 다시 시도해주세요.',
  CONNECTION_TIMEOUT: '응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.',
  AUTH_FAILED: '인증이 만료되었습니다. 다시 로그인해주세요.',
  UNKNOWN_ERROR: '일시적인 오류가 발생했습니다. 다시 시도해주세요.',
  CLIENT_ERROR: (status: number) => `요청에 실패했습니다 (${status}). 잠시 후 다시 시도해주세요.`,
  SERVER_ERROR: (status: number) => `서버에 일시적인 문제가 발생했습니다 (${status}). 잠시 후 다시 시도해주세요.`,
} as const;

const NODE_PROGRESS_FALLBACK: Record<string, string> = {
  campaign_context_builder: '캠페인 컨텍스트를 구성하고 있습니다...',
  campaign_planner: '캠페인 기본 정보를 생성하고 있습니다...',
  set_composer: '발송세트를 구성하고 있습니다...',
  message_strategy_generator: '메시지 전략을 작성하고 있습니다...',
  message_generator: '메시지를 생성하고 있습니다...',
  message_draft_validator: '메시지 품질을 확인하고 있습니다...',
  campaign_draft_validator: '캠페인 초안을 검토하고 있습니다...',
};

/**
 * SSE content 데이터에서 문자열 추출
 * - 문자열이면 그대로 반환
 * - 객체면 text/message 필드 추출
 * - 그 외는 빈 문자열 반환
 */
export function extractSSEContent(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }

  if (typeof content === 'object' && content !== null) {
    const obj = content as Record<string, unknown>;
    return (obj.text || obj.message || '') as string;
  }

  return '';
}

/**
 * SSE error 메시지에서 문자열 추출
 * - 문자열이면 그대로 반환
 * - 그 외는 기본 에러 메시지 반환
 */
export function extractSSEErrorMessage(
  message: unknown,
  fallback: string = SSE_ERROR_MESSAGES.UNKNOWN_ERROR,
): string {
  return typeof message === 'string' ? message : fallback;
}

/**
 * SSE progress 메시지에서 문자열 추출
 * - 문자열이면 그대로 반환
 * - 그 외는 node 정보로 기본 메시지 생성
 */
export function extractSSEProgressMessage(message: unknown, node?: string): string {
  if (typeof message === 'string') {
    return message;
  }
  return NODE_PROGRESS_FALLBACK[node || ''] || '처리 중입니다...';
}
